/*
 * Rapunzel theme — único archivo JS (cargado con defer, sin dependencias).
 * Módulos: diálogos (menú, carrito, zoom, buscador) · header al hacer scroll · submenús · carrito AJAX ·
 * selector de variantes/packs · cantidad · galería · barra fija móvil · agregar rápido (mismo handler) ·
 * productos relacionados · formularios que se envían solos (filtros, idioma) · casos de éxito · WhatsApp flotante ·
 * aparición al scroll.
 * Analítica: no se agregan scripts de seguimiento; los eventos estándar de Shopify
 * (view_item, add_to_cart, begin_checkout) se conservan usando el formulario y las rutas nativas.
 */
(() => {
  'use strict';

  const strings = window.rpStrings || {};
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);

  /* ---------------------------------------------------------------- Diálogos */

  const openDialog = (dialog, opener) => {
    if (!dialog || dialog.open) return;
    dialog.showModal();
    if (opener) {
      opener.setAttribute('aria-expanded', 'true');
      dialog._rpOpener = opener;
    }
  };

  const closeDialog = (dialog) => {
    if (!dialog || !dialog.open) return;
    dialog.close();
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-rp-open-dialog]');
    if (opener) {
      event.preventDefault();
      openDialog(document.getElementById(opener.dataset.rpOpenDialog), opener);
      return;
    }

    const closer = event.target.closest('[data-rp-close-dialog]');
    if (closer) {
      const dialog = closer.closest('dialog');
      if (dialog) closeDialog(dialog);
      return;
    }

    // Clic en el fondo (backdrop) cierra el diálogo.
    if (event.target.matches('dialog[data-rp-dialog][open]')) {
      const rect = event.target.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left && event.clientX <= rect.right &&
        event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) closeDialog(event.target);
    }
  });

  document.addEventListener(
    'close',
    (event) => {
      const dialog = event.target;
      if (!(dialog instanceof HTMLDialogElement) || !dialog._rpOpener) return;
      dialog._rpOpener.setAttribute('aria-expanded', 'false');
      dialog._rpOpener = null;
    },
    true
  );

  /* ------------------------------------------------------------------ Header */

  const headerWrapper = document.querySelector('.rp-header-wrapper');
  if (headerWrapper) {
    let ticking = false;
    const update = () => {
      headerWrapper.classList.toggle('is-scrolled', window.scrollY > 4);
      ticking = false;
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          window.requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );
    update();
  }

  /* ----------------------------------------------------------------- Carrito */

  const getDrawer = () => document.querySelector('[data-rp-cart-drawer]');

  const renderCart = (sectionHtml) => {
    const drawer = getDrawer();
    if (!drawer || !sectionHtml) return;
    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const next = doc.querySelector('[data-rp-cart-contents]');
    const current = drawer.querySelector('[data-rp-cart-contents]');
    if (!next || !current) return;

    const hadFocus = current.contains(document.activeElement);
    current.innerHTML = next.innerHTML;

    const count = Number(next.dataset.cartCount || 0);
    current.dataset.cartCount = String(count);
    drawer.toggleAttribute('data-empty', count === 0);
    document.querySelectorAll('[data-rp-cart-count]').forEach((bubble) => {
      bubble.textContent = String(count);
      bubble.hidden = count === 0;
    });

    if (hadFocus) {
      const target = drawer.querySelector('[data-rp-cart-contents] button, [data-rp-cart-contents] a') ||
        drawer.querySelector('[data-rp-close-dialog]');
      if (target) target.focus();
    }
  };

  const sectionsPayload = () => ({ sections: 'cart-drawer', sections_url: window.location.pathname });

  const setBusy = (buttons, busy) => {
    buttons.forEach((button) => {
      button.classList.toggle('is-loading', busy);
      button.setAttribute('aria-busy', String(busy));
      if (busy) button.disabled = true;
      else if (button.dataset.soldOut !== 'true') button.disabled = false;
    });
  };

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-rp-product-form]');
    if (!form || !window.fetch) return;
    event.preventDefault();

    const buttons = [
      ...form.querySelectorAll('[data-rp-add-button]'),
      ...(form.id ? document.querySelectorAll(`[data-rp-add-button][form="${form.id}"]`) : []),
    ];
    const errorBox = form.querySelector('[data-rp-form-error]');
    if (errorBox) errorBox.hidden = true;
    setBusy(buttons, true);

    const body = new FormData(form);
    const payload = sectionsPayload();
    body.append('sections', payload.sections);
    body.append('sections_url', payload.sections_url);

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || strings.addError);
      }
      renderCart(data.sections && data.sections['cart-drawer']);
      openDialog(getDrawer());
    } catch (error) {
      if (errorBox) {
        errorBox.textContent = (error && error.message) || strings.addError || '';
        errorBox.hidden = false;
      }
    } finally {
      setBusy(buttons, false);
    }
  });

  document.addEventListener('click', async (event) => {
    const toggle = event.target.closest('[data-rp-cart-toggle]');
    if (toggle && getDrawer()) {
      event.preventDefault();
      openDialog(getDrawer(), toggle);
      return;
    }

    const change = event.target.closest('[data-rp-cart-change]');
    if (!change) return;
    event.preventDefault();
    const drawer = getDrawer();
    drawer && drawer.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: change.dataset.key,
          quantity: Number(change.dataset.quantity),
          ...sectionsPayload(),
        }),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message);
      renderCart(data.sections && data.sections['cart-drawer']);
    } catch (error) {
      const box = drawer && drawer.querySelector('[data-rp-cart-error]');
      if (box) {
        box.textContent = (error && error.message) || strings.cartError || '';
        box.hidden = false;
      }
    } finally {
      drawer && drawer.removeAttribute('aria-busy');
    }
  });

  /* ------------------------------------------------- Producto: packs y galería */

  const initGallery = (gallery) => {
    const track = gallery.querySelector('[data-rp-gallery-track]');
    if (!track) return null;
    const slides = [...track.querySelectorAll('.rp-gallery__slide')];
    const thumbs = [...gallery.querySelectorAll('[data-rp-gallery-thumb]')];
    const dots = [...gallery.querySelectorAll('.rp-gallery__dot')];
    const status = gallery.querySelector('[data-rp-gallery-status]');

    const setActive = (index) => {
      thumbs.forEach((thumb, i) => thumb.toggleAttribute('aria-current', i === index));
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
      if (status && strings.galleryStatus) {
        status.textContent = strings.galleryStatus
          .replace('[current]', index + 1)
          .replace('[total]', slides.length);
      }
    };

    const goTo = (index) => {
      const slide = slides[index];
      if (!slide) return;
      track.scrollTo({ left: slide.offsetLeft, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      setActive(index);
    };

    thumbs.forEach((thumb) => thumb.addEventListener('click', () => goTo(Number(thumb.dataset.rpGalleryThumb))));

    if ('IntersectionObserver' in window && slides.length > 1) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) setActive(Number(entry.target.dataset.index));
          });
        },
        { root: track, threshold: 0.6 }
      );
      slides.forEach((slide) => observer.observe(slide));
    }

    return {
      goToMedia(mediaId) {
        if (!mediaId) return;
        const index = slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
        if (index > -1) goTo(index);
      },
    };
  };

  const initZoom = (scope) => {
    const dialog = scope.querySelector('[data-rp-zoom]');
    if (!dialog || dialog._rpReady) return;
    dialog._rpReady = true;
    const stage = dialog.querySelector('[data-rp-zoom-stage]');
    const image = document.createElement('img');
    image.className = 'rp-zoom__image';
    stage.appendChild(image);
    scope.querySelectorAll('[data-rp-zoom-src]').forEach((button) => {
      button.addEventListener('click', () => {
        image.src = button.dataset.rpZoomSrc;
        image.alt = button.dataset.rpZoomAlt || '';
        openDialog(dialog, button);
      });
    });
  };

  // Barra fija: aparece cuando `trigger` (bloque de compra o acciones del hero) salió de la pantalla por arriba.
  const initStickyBar = (bar, trigger) => {
    if (!bar || !trigger || bar._rpReady || !('IntersectionObserver' in window)) return;
    bar._rpReady = true;
    const observer = new IntersectionObserver(([entry]) => {
      const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', show);
      bar.inert = !show;
      document.body.classList.toggle('rp-has-sticky-buy', show);
    });
    observer.observe(trigger);
  };

  const initProduct = (scope) => {
    if (scope._rpReady) return;
    scope._rpReady = true;

    const galleryEl = scope.querySelector('[data-rp-gallery]');
    const gallery = galleryEl ? initGallery(galleryEl) : null;
    initZoom(scope);
    initStickyBar(scope.querySelector('[data-rp-sticky-buy]:not([data-rp-sticky-for])'), scope.querySelector('[data-rp-main-buy]'));

    // Aplica una variante al panel de compra. `variant` = {id, available, price, compare, title, mediaId, image}
    // o null cuando la combinación de opciones no existe.
    const applyVariant = (form, variant) => {
      if (!form) return;
      const available = Boolean(variant && variant.available);

      const idInput = form.querySelector('[data-rp-variant-id]');
      if (idInput && variant && variant.id) {
        idInput.value = variant.id;
        // El pago exprés escucha este cambio: así compra el pack seleccionado.
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (variant) {
        scope.querySelectorAll('[data-rp-price]').forEach((el) => { el.textContent = variant.price; });
        scope.querySelectorAll('[data-rp-compare]').forEach((el) => {
          el.textContent = variant.compare || '';
          el.hidden = !variant.compare;
        });
        scope.querySelectorAll('[data-rp-variant-title]').forEach((el) => { el.textContent = variant.title; });

        const stickyImage = scope.querySelector('[data-rp-sticky-image]');
        if (stickyImage && variant.image) {
          stickyImage.removeAttribute('srcset');
          stickyImage.src = variant.image;
        }
      }

      const buttons = [
        ...form.querySelectorAll('[data-rp-add-button]'),
        ...document.querySelectorAll(`[data-rp-add-button][form="${form.id}"]`),
      ];
      buttons.forEach((button) => {
        const label = button.querySelector('[data-rp-add-label]');
        button.dataset.soldOut = String(!available);
        button.disabled = !available;
        if (!label) return;
        if (!variant) label.textContent = strings.unavailable || button.dataset.labelSoldOut;
        else if (!available) label.textContent = button.dataset.labelSoldOut;
        else if ('noPrice' in button.dataset) label.textContent = button.dataset.labelAdd;
        else label.textContent = (strings.addWithPrice || '[label] · [price]')
          .replace('[label]', button.dataset.labelAdd)
          .replace('[price]', variant.price);
      });

      if (gallery && variant) gallery.goToMedia(variant.mediaId);

      if (variant && variant.id && scope.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    };

    // Packs (1 opción): un radio por variante con sus datos en data-*.
    scope.querySelectorAll('[data-rp-packs]').forEach((fieldset) => {
      const form = document.getElementById(fieldset.dataset.formId);
      fieldset.addEventListener('change', (event) => {
        const radio = event.target.closest('input[type="radio"]');
        if (!radio) return;
        applyVariant(form, {
          id: radio.value,
          available: radio.dataset.available === 'true',
          price: radio.dataset.price,
          compare: radio.dataset.compare || '',
          title: radio.dataset.title,
          mediaId: radio.dataset.mediaId,
          image: radio.dataset.image,
        });
      });
    });

    // Opciones (2+): se resuelve la variante con el JSON generado por Liquid.
    scope.querySelectorAll('[data-rp-options]').forEach((picker) => {
      const form = document.getElementById(picker.dataset.formId);
      const script = picker.querySelector('[data-rp-variants]');
      let variants = [];
      try { variants = JSON.parse(script ? script.textContent : '[]'); } catch (error) { return; }
      const groups = [...picker.querySelectorAll('fieldset')];
      const selected = () => groups.map((group) => {
        const radio = group.querySelector('input:checked');
        return radio ? radio.value : null;
      });

      // Tacha los valores que no tienen variante disponible con el resto de opciones elegidas.
      const markAvailability = () => {
        const current = selected();
        groups.forEach((group, index) => {
          group.querySelectorAll('input[type="radio"]').forEach((radio) => {
            const ok = variants.some((v) => v.available && v.options.every((value, i) => (
              i === index ? value === radio.value : value === current[i]
            )));
            const label = picker.querySelector(`label[for="${radio.id}"]`);
            if (label) label.classList.toggle('rp-option--unavailable', !ok);
          });
        });
      };

      picker.addEventListener('change', () => {
        const current = selected();
        const match = variants.find((v) => v.options.every((value, i) => value === current[i]));
        applyVariant(form, match || null);
        markAvailability();
      });
      markAvailability();
    });
  };

  // Cantidad ± (delegado: sirve para cualquier [data-rp-qty] de la página).
  document.addEventListener('click', (event) => {
    const step = event.target.closest('[data-rp-qty-step]');
    if (!step) return;
    const input = step.closest('[data-rp-qty]').querySelector('[data-rp-qty-input]');
    if (!input) return;
    const min = Number(input.min || 1);
    input.value = Math.max(min, (Number(input.value) || min) + Number(step.dataset.rpQtyStep));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ----------------------------------------------------------- Casos de éxito */

  const expandCases = (section) => {
    if (section.classList.contains('is-expanded')) return;
    section.classList.add('is-expanded');
    const more = section.querySelector('[data-rp-cases-more]');
    if (more) {
      more.setAttribute('aria-expanded', 'true');
      more.parentElement.hidden = true;
    }
  };

  const initCases = (section) => {
    if (section._rpReady) return;
    section._rpReady = true;
    initZoom(section);

    const more = section.querySelector('[data-rp-cases-more]');
    if (!more) return;
    more.addEventListener('click', () => {
      expandCases(section);
      // El foco pasa a la primera foto nueva para seguir navegando con teclado.
      const first = section.querySelector('.rp-cases__item--extra .rp-case__media');
      if (first) first.focus({ preventScroll: true });
    });
  };

  /* --------------------------------------------------------------- Compartir */

  const initShare = (share) => {
    if (share._rpReady) return;
    share._rpReady = true;
    const { url, text } = share.dataset;
    const status = share.querySelector('[data-rp-share-status]');

    const nativeItem = share.querySelector('[data-rp-share-native-item]');
    if (nativeItem && navigator.share) {
      nativeItem.hidden = false;
      nativeItem.querySelector('[data-rp-share-native]').addEventListener('click', () => {
        // Cerrar el menú sin compartir rechaza la promesa: no es un error.
        navigator.share({ title: text, text, url }).catch(() => {});
      });
    }

    const copy = share.querySelector('[data-rp-share-copy]');
    if (!copy) return;
    copy.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(url);
        status.textContent = strings.shareCopied || '';
      } catch (error) {
        status.textContent = strings.shareCopyError || '';
      }
      clearTimeout(share._rpStatusTimer);
      share._rpStatusTimer = setTimeout(() => { status.textContent = ''; }, 3000);
    });
  };

  /* ----------------------------------------------------------------- WhatsApp */

  // Botón flotante (snippets/whatsapp-widget.liquid): el mensaje lleva la URL exacta que ve el visitante.
  // Parámetros de campaña que no aportan al mensaje; se conservan ?variant=, el resto y el hash.
  const TRACKING_PARAM = /^(utm_|fbclid$|gclid$|gbraid$|wbraid$|msclkid$|ttclid$)/;
  const WHATSAPP_BUBBLE_KEY = 'rp-whatsapp-bubble';

  const cleanUrl = () => {
    const url = new URL(window.location.href);
    [...url.searchParams.keys()].forEach((key) => {
      if (TRACKING_PARAM.test(key)) url.searchParams.delete(key);
    });
    return url.toString();
  };

  const initWhatsapp = (widget) => {
    if (widget._rpReady) return;
    widget._rpReady = true;
    const { number, message } = widget.dataset;
    const links = [...widget.querySelectorAll('[data-rp-whatsapp-link]')];

    // La URL definitiva se arma acá: lleva el ?variant= que escribe applyVariant con replaceState.
    const refresh = () => {
      const text = [message, cleanUrl()].filter(Boolean).join('\n');
      const href = `https://wa.me/${number}?text=${encodeURIComponent(text)}`;
      links.forEach((link) => { link.href = href; });
    };
    refresh();
    // pointerdown cubre «copiar enlace» con pulsación larga o clic derecho; click, el teclado.
    links.forEach((link) => ['pointerdown', 'click'].forEach((type) => link.addEventListener(type, refresh)));

    const bubble = widget.querySelector('[data-rp-whatsapp-bubble]');
    if (!bubble) return;
    const remembered = () => {
      try { return window.sessionStorage.getItem(WHATSAPP_BUBBLE_KEY) === '1'; } catch (error) { return false; }
    };
    const dismiss = () => {
      bubble.hidden = true;
      try { window.sessionStorage.setItem(WHATSAPP_BUBBLE_KEY, '1'); } catch (error) { /* sin almacenamiento: solo se oculta */ }
    };
    widget.querySelector('[data-rp-whatsapp-close]').addEventListener('click', () => {
      dismiss();
      const button = widget.querySelector('[data-rp-whatsapp-button]');
      if (button) button.focus();
    });
    links.forEach((link) => link.addEventListener('click', dismiss)); // ya abrió el chat: no insistir
    if (remembered()) return;
    const delay = designMode ? 0 : Number(widget.dataset.delay || 0) * 1000;
    setTimeout(() => { bubble.hidden = false; }, delay);
  };

  /* ------------------------------------------------------- Aparición al scroll */

  const initReveal = (scope) => {
    const items = [...scope.querySelectorAll('[data-rp-reveal]:not(.is-revealed)')];
    if (!items.length) return;
    const revealAll = () => items.forEach((item) => item.classList.add('is-revealed'));
    if (designMode || reducedMotion.matches || !('IntersectionObserver' in window) ||
        !document.body.classList.contains('rp-reveal-enabled')) {
      revealAll();
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    );
    items.forEach((item) => observer.observe(item));
  };

  /* ------------------------------------------- Formularios que se envían solos */

  // <select> / <input data-rp-autosubmit>: orden y filtros de colección, selector de idioma.
  // Sin JS queda el botón de envío del formulario.
  document.addEventListener('change', (event) => {
    const field = event.target.closest('[data-rp-autosubmit]');
    if (!field || !field.form) return;
    if (field.form.requestSubmit) field.form.requestSubmit();
    else field.form.submit();
  });

  /* --------------------------------------------------------- Submenús del header */

  // <details data-rp-nav-sub>: solo uno abierto; se cierran al hacer clic fuera o con Escape.
  const closeNavSubs = (except) => {
    document.querySelectorAll('details[data-rp-nav-sub][open]').forEach((details) => {
      if (details !== except) details.open = false;
    });
  };
  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (details instanceof HTMLDetailsElement && details.matches('[data-rp-nav-sub]') && details.open) {
        closeNavSubs(details);
      }
    },
    true
  );
  document.addEventListener('click', (event) => {
    if (!event.target.closest('details[data-rp-nav-sub]')) closeNavSubs();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeNavSubs();
  });

  /* ------------------------------------------------------ Productos relacionados */

  // Pide la sección product-recommendations ya renderizada por Shopify y la inserta (Section Rendering API).
  const initRecommendations = async (wrapper) => {
    if (wrapper._rpReady || !wrapper.dataset.url || !window.fetch) return;
    wrapper._rpReady = true;
    try {
      const response = await fetch(wrapper.dataset.url);
      if (!response.ok) return;
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const next = doc.querySelector('[data-rp-recommendations]');
      if (!next || !next.innerHTML.trim()) return;
      wrapper.innerHTML = next.innerHTML;
      init(wrapper);
    } catch (error) {
      // Sin recomendaciones: la sección queda vacía.
    }
  };

  /* -------------------------------------------------------------- Arranque */

  const init = (scope = document) => {
    scope.querySelectorAll('[data-rp-product]').forEach(initProduct);
    scope.querySelectorAll('[data-rp-cases]').forEach(initCases);
    scope.querySelectorAll('[data-rp-share]').forEach(initShare);
    scope.querySelectorAll('[data-rp-whatsapp]').forEach(initWhatsapp);
    scope.querySelectorAll('[data-rp-recommendations]').forEach(initRecommendations);
    scope.querySelectorAll('[data-rp-sticky-buy][data-rp-sticky-for]').forEach((bar) => {
      initStickyBar(bar, document.getElementById(bar.dataset.rpStickyFor));
    });
    initReveal(scope);
  };

  init();

  // Editor de temas: re-inicializar secciones y abrir el acordeón (o el «Ver más») del bloque seleccionado.
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const details = event.target.closest('details') || event.target.querySelector('details');
    if (details) details.open = true;
    const cases = event.target.closest('[data-rp-cases]');
    if (cases) expandCases(cases);
  });
})();
